/**
 * Pets (สัตว์เลี้ยง): companions that walk at their owner's heel. The art reference is
 * docs/art/pets-ref (20 pets in white or dark coats with red and gold harness).
 *
 * The world has one pet today, the nine-tailed fox everyone starts with (world.ts `pet`, drawn by
 * its 2D `sprite`). The rest are data for the pet system and the 3D models to come. A pet of an
 * animal that is also a mount shares its model family (data/mounts `modelFamily`).
 */
import type { Earn } from './earn';
import type { Element } from './elements';

/** What a pet is like: how it follows, greets and fights. */
export type PetTemper = 'loyal' | 'fierce' | 'gentle' | 'playful' | 'proud' | 'calm' | 'cunning';

export const PET_TEMPER_LABELS: Readonly<Record<PetTemper, string>> = {
  loyal: 'ภักดี',
  fierce: 'ดุดัน',
  gentle: 'อ่อนโยน',
  playful: 'ขี้เล่น',
  proud: 'ทะนง',
  calm: 'สุขุม',
  cunning: 'เจ้าเล่ห์',
};

/** The moves a pet's model has (each key poses plus a motion, 14 frames). */
export type PetMove = 'stand' | 'walk' | 'run' | 'fly' | 'swim' | 'attack';

export interface PetDef {
  readonly id: string;
  readonly name: string;
  readonly desc: string;
  readonly temper: PetTemper;
  /** The element it leans to (data/elements), shown with its temper. */
  readonly element: Element;
  /** Its moves, in the order of the reference sheet. */
  readonly moves: readonly PetMove[];
  /** How it is earned (data/earn). */
  readonly earn: Earn;
  /** The 3D model: `pet-<family>-<variant>` (see data/mounts `MODEL_ID`). */
  readonly modelId: string;
  /** The 2D sprite (render/sprites) while the game is drawn in 2D: only the fox has one. */
  readonly sprite?: string;
}

const RUNS: readonly PetMove[] = ['stand', 'walk', 'run', 'attack'];
const FLIES: readonly PetMove[] = ['stand', 'walk', 'fly', 'attack'];

const list: PetDef[] = [
  {
    id: 'war-horse',
    name: 'ม้าศึก',
    desc: 'ม้าศึกขาวผ่องสวมเครื่องม้าแดงทอง ตัวเล็กกว่าม้าขี่แต่ใจกล้าไม่แพ้กัน เดินเคียงเจ้าของเหมือนทหารคุ้มกัน',
    temper: 'loyal',
    element: 'yang',
    moves: RUNS,
    earn: { via: 'shop', gold: 1500 },
    modelId: 'pet-horse-war',
  },
  {
    id: 'little-dragon',
    name: 'มังกรน้อย',
    desc: 'มังกรแดงตัวน้อยเกล็ดขลิบทอง ลอยขดตัวอยู่กลางอากาศ พ่นควันออกจมูกเวลางอน',
    temper: 'proud',
    element: 'yang',
    moves: FLIES,
    earn: { via: 'mall', jade: 1280 },
    modelId: 'pet-dragon-red',
  },
  {
    id: 'white-tiger',
    name: 'เสือขาว',
    desc: 'ลูกเสือขาวลายดำ ปลอกคออานแดง ตำนานว่าเสือขาวคือผู้พิทักษ์ทิศตะวันตก มันจึงนั่งหันไปทางนั้นเสมอ',
    temper: 'fierce',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'pass', season: 's1', tier: 20, track: 'premium' },
    modelId: 'pet-tiger-white',
  },
  {
    id: 'black-tiger',
    name: 'เสือดำ',
    desc: 'เสือดำขนมันลายทอง ตาสีอำพัน เดินเงียบเหมือนเงา ศัตรูรู้ตัวอีกทีก็เมื่อมันยืนอยู่ข้างหลังแล้ว',
    temper: 'fierce',
    element: 'yin',
    moves: RUNS,
    earn: { via: 'mall', jade: 980 },
    modelId: 'pet-tiger-black',
  },
  {
    id: 'white-wolf',
    name: 'หมาป่าสีขาว',
    desc: 'ลูกหมาป่าขาวที่รอดจากฝูงซึ่งถูกปราณมารปลุก มันจำผู้ที่ไว้ชีวิตมันได้ และเห่าเตือนเมื่อปราณมารอยู่ใกล้',
    temper: 'loyal',
    element: 'soft',
    moves: RUNS,
    earn: { via: 'quest', quest: 'wolves' },
    modelId: 'pet-wolf-white',
  },
  {
    id: 'black-wolf',
    name: 'หมาป่าสีดำ',
    desc: 'หมาป่าดำขนฟู ผ้าพันคอแดง ไม่ค่อยเห่าแต่ไม่เคยห่างเจ้าของเกินสามก้าว',
    temper: 'cunning',
    element: 'yin',
    moves: RUNS,
    earn: { via: 'pass', season: 's1', tier: 25, track: 'free' },
    modelId: 'pet-wolf-black',
  },
  {
    id: 'nine-tailed-fox',
    name: 'จิ้งจอกเก้าหาง',
    desc: 'จิ้งจอกขาวเก้าหางปลายแดง อยู่ข้างกายมาตั้งแต่วันที่ตื่นขึ้นที่ท่าเรือ ไม่มีใครรู้ว่ามันมาจากไหน แต่มันรู้ทางเสมอ',
    temper: 'playful',
    element: 'yin',
    moves: FLIES,
    earn: { via: 'start' },
    modelId: 'pet-fox-ninetail',
    sprite: 'fox',
  },
  {
    id: 'spirit-stag',
    name: 'กวางวิเศษ',
    desc: 'กวางขาวเขาทองเดินบนอากาศได้ ปรากฏตัวให้เห็นเฉพาะผู้ที่เก็บสมุนไพรโดยไม่ทำร้ายภูเขา',
    temper: 'calm',
    element: 'soft',
    moves: FLIES,
    earn: { via: 'profession', profession: 'herbalist', level: 5 },
    modelId: 'pet-stag-spirit',
  },
  {
    id: 'young-eagle',
    name: 'นกอินทรี',
    desc: 'ลูกนกอินทรีภูเขา เกาะไหล่เจ้าของแล้วบินวนเหนือหัวเมื่อเห็นศัตรู ตาไวกว่ายามรักษาการณ์คนใด',
    temper: 'proud',
    element: 'yang',
    moves: FLIES,
    earn: { via: 'mall', jade: 480 },
    modelId: 'pet-eagle-brown',
  },
  {
    id: 'phoenix',
    name: 'หงส์',
    desc: 'หงส์ขาวขนปลายแดงดุจเปลวไฟ หางยาวลากเป็นริ้ว นกมงคลที่ไม่ยอมอยู่กับผู้ใดนาน เว้นแต่ผู้ที่ไม่เคยถอยหนี',
    temper: 'proud',
    element: 'yang',
    moves: RUNS,
    earn: {
      via: 'legend',
      how: 'ตำนาน: ไต่บันไดสวรรค์ถึงขั้นเทพ และจบวันที่อันดับ 1 ของประลองยุทธ',
    },
    modelId: 'pet-phoenix-red',
  },
  {
    id: 'spirit-rabbit',
    name: 'กระต่ายวิเศษ',
    desc: 'กระต่ายขาวหูยาวผูกโบแดง กระโดดตามคันนา ว่ากันว่าเป็นกระต่ายจากดวงจันทร์ที่ลงมาตำยาให้ชาวนา',
    temper: 'playful',
    element: 'soft',
    moves: RUNS,
    earn: { via: 'profession', profession: 'farmer', level: 3 },
    modelId: 'pet-rabbit-spirit',
  },
  {
    id: 'panda-cub',
    name: 'แพนด้า',
    desc: 'ลูกแพนด้าสวมผ้าคลุมแดงทอง เดินโยกเยกตามหลัง เมื่อเหนื่อยก็นั่งลงเคี้ยวไผ่จนเจ้าของต้องกลับมาอุ้ม',
    temper: 'gentle',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'pass', season: 's1', tier: 5, track: 'premium' },
    modelId: 'pet-panda-cub',
  },
  {
    id: 'bear',
    name: 'หมี',
    desc: 'ลูกหมีดำภูเขาที่ไม่ถูกปราณมารครอบงำ ตัวหนาขนฟู หลังคาดอานแดง ตบทีเดียวต้นไผ่หักครึ่ง',
    temper: 'fierce',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'quest', quest: 'bears' },
    modelId: 'pet-bear-brown',
  },
  {
    id: 'white-crane',
    name: 'นกกระเรียน',
    desc: 'กระเรียนขาวหัวแดงขายาว ยืนขาเดียวริมน้ำได้ทั้งวัน ชาวประมงถือว่าเป็นนกนำโชค',
    temper: 'calm',
    element: 'taiji',
    moves: RUNS,
    earn: { via: 'profession', profession: 'fisher', level: 5 },
    modelId: 'pet-crane-red',
  },
  {
    id: 'dragon-turtle',
    name: 'เต่ามังกร',
    desc: 'เต่ากระดองหยกหัวมังกร เดินช้าแต่ไม่เคยหลง แบกโชคลาภไว้บนหลัง ใครเลี้ยงไว้ว่ากันว่าอายุยืน',
    temper: 'calm',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'pass', season: 's1', tier: 20, track: 'free' },
    modelId: 'pet-turtle-dragon',
  },
  {
    id: 'lion-dancer',
    name: 'สิงโตเชิด',
    desc: 'สิงโตขาวแผงคอฟูประดับแดงทอง กระโดดโลดเต้นเหมือนในงานเทศกาล เสียงฆ้องดังที่ไหนมันวิ่งไปที่นั่น',
    temper: 'playful',
    element: 'yang',
    moves: RUNS,
    earn: { via: 'mall', jade: 880 },
    modelId: 'pet-lion-dance',
  },
  {
    id: 'little-qilin',
    name: 'กิเลนน้อย',
    desc: 'กิเลนขาวเขาทองตัวน้อย ขนแผงพลิ้วดุจเปลวไฟ เหยียบที่ใดดอกไม้บานที่นั่น สัตว์มงคลที่เลือกเจ้าของเอง',
    temper: 'proud',
    element: 'taiji',
    moves: RUNS,
    earn: {
      via: 'legend',
      how: 'ตำนาน: ได้ฉายาตำนานเก้ากระบี่',
    },
    modelId: 'pet-qilin-gold',
  },
  {
    id: 'boar',
    name: 'หมูป่า',
    desc: 'หมูป่าเขี้ยวโค้ง หลังคาดผ้าแดง ขุดรากไม้ในป่าหาของกินเอง วิ่งชนทีไรศัตรูล้มทั้งยืน',
    temper: 'fierce',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'profession', profession: 'woodcutter', level: 4 },
    modelId: 'pet-boar-wild',
  },
  {
    id: 'divine-ox',
    name: 'วัวเทพ',
    desc: 'วัวเขาทองคาดผ้าแดง ว่ากันว่าเคยไถนาให้เทพเจ้า ชาวนาผู้ชำนาญที่สุดเท่านั้นที่มันยอมเดินตาม',
    temper: 'calm',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'profession', profession: 'farmer', level: 10 },
    modelId: 'pet-ox-divine',
  },
  {
    id: 'dragon-carp',
    name: 'ปลามังกร',
    desc: 'ปลาคาร์ปแดงขาวครีบยาวดุจผ้าไหม ว่ายตามเจ้าของได้ทั้งในน้ำและกลางอากาศ ตำนานว่าวันหนึ่งมันจะกระโดดข้ามประตูมังกร',
    temper: 'gentle',
    element: 'soft',
    moves: ['stand', 'swim', 'attack'],
    earn: { via: 'profession', profession: 'fisher', level: 9 },
    modelId: 'pet-carp-dragon',
  },
  // ── the world bosses' chests (data/worldBosses.ts rare rolls; redeemed from a token item, data/bossGear.ts) ──
  // Model ids are placeholders until the family is rigged (docs/handoff/newdir/ITEMS-V2.md).
  {
    id: 'ape-cub',
    name: 'ลูกวานรทงปี้',
    desc: 'ลูกลิงยักษ์ขนเงินจากผาทงปี้ ห้อยหัวตามเจ้าของไปทุกที่ ชอบแอบหยิบกระดิ่งของคนอื่น',
    temper: 'playful',
    element: 'hard',
    moves: RUNS,
    earn: { via: 'boss', boss: 'wb-ape', how: 'หีบบอสโลก: ลิงยักษ์ทงปี้ (โอกาสสุ่ม 3%)' },
    modelId: 'pet-ape-cub',
  },
  {
    id: 'firebird-chick',
    name: 'ลูกนกเพลิง',
    desc: 'ลูกนกเพลิงตัวเท่ากำปั้น ขนปลายเป็นเปลวไฟอ่อนๆ เกาะบ่าเจ้าของแล้วอุ่นไปทั้งตัว',
    temper: 'proud',
    element: 'yang',
    moves: FLIES,
    earn: { via: 'boss', boss: 'wb-firebird', how: 'หีบบอสโลก: นกเพลิงปี้ฟาง (โอกาสสุ่ม 3%)' },
    modelId: 'pet-firebird-chick',
  },
  {
    id: 'turtle-hatchling',
    name: 'ลูกเต่าคะนอง',
    desc: 'ลูกเต่ากระดองลายสายฟ้า คืบคลานช้าๆ แต่ไม่เคยตกหลังเจ้าของ มีประกายไฟฟ้าแปลบเวลางอน',
    temper: 'calm',
    element: 'yin',
    moves: RUNS,
    earn: { via: 'boss', boss: 'wb-thunder-turtle', how: 'หีบบอสโลก: เต่าคะนอง (โอกาสสุ่ม 4%)' },
    modelId: 'pet-turtle-hatchling',
  },
];

export const PETS: Readonly<Record<string, PetDef>> = Object.fromEntries(list.map((p) => [p.id, p]));
export const PET_LIST: readonly PetDef[] = list;

export function getPet(id: string): PetDef {
  const p = PETS[id];
  if (!p) throw new Error(`Unknown pet: ${id}`);
  return p;
}

/** The pet every character has from the start (the fox at the player's heel). */
export const STARTER_PET = 'nine-tailed-fox';

/**
 * The world's pet entity follows the pet picked (`petPick`): its `name` is the pet's (the renderer chooses the model
 * by it until the pets have a sprite key); the starter fox keeps the empty name every entity starts with, so a new character's world is unchanged.
 */
export function followPetPick(pet: { name?: string }, pick: string): void {
  if (pick === STARTER_PET || !PETS[pick]) pet.name = '';
  else pet.name = PETS[pick]!.name;
}
